import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { PublicPageLayout } from '../components/ui/PublicPageLayout';

export const RefundPolicyPage: React.FC = () => {
  return (
    <PublicPageLayout className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
          >
            ← Back to Home
          </Link>
        </div>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Refund Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              <strong>Company:</strong> TECKVINI SOLUTIONS PRIVATE LIMITED<br/>
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Refund Eligibility</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  We understand that sometimes you may need to request a refund. Our refund policy is 
                  designed to be fair while protecting against abuse of our services.
                </p>
                <p>
                  <strong>Refunds are available in the following circumstances:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Technical issues preventing service access</li>
                  <li>Duplicate payments made in error</li>
                  <li>Service not delivered as described</li>
                  <li>Account suspended due to system error</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Refund Timeframes</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  <strong>Processing Time:</strong> 3-5 business days after approval
                </p>
                <p>
                  <strong>Refund Method:</strong> Refunds are processed to the original payment method used for the purchase.
                </p>
                <p>
                  <strong>Bank Processing:</strong> Additional 2-7 business days may be required for the refund to appear in your account, depending on your bank.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Non-Refundable Items</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  The following are generally not eligible for refunds:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Int Coins that have been used for searches</li>
                  <li>Services that have been successfully delivered</li>
                  <li>Refund requests made more than 30 days after purchase</li>
                  <li>Accounts terminated for violation of terms of service</li>
                  <li>Refund requests due to change of mind</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Partial Refunds</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  In some cases, we may offer partial refunds:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If you have used some but not all of your Int Coins</li>
                  <li>If there were partial service disruptions</li>
                  <li>If you received partial access to purchased features</li>
                </ul>
                <p>
                  Partial refunds are calculated based on the unused portion of your purchase.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">How to Request a Refund</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  To request a refund, please follow these steps:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Contact our support team at support@intfinder.com</li>
                  <li>Include your account email and order details</li>
                  <li>Provide a clear explanation of why you're requesting a refund</li>
                  <li>Include any relevant screenshots or error messages</li>
                </ol>
                <p>
                  <strong>Response Time:</strong> We will respond to your refund request within 24-48 hours.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Refund Processing</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  Once your refund is approved:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We will process the refund within 3-5 business days</li>
                  <li>You will receive an email confirmation</li>
                  <li>Your account may be suspended during the refund process</li>
                  <li>Any remaining Int Coins will be removed from your account</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Dispute Resolution</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  If you disagree with our refund decision, you can:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request a review by our management team</li>
                  <li>Provide additional documentation to support your case</li>
                  <li>Contact us through our escalation process</li>
                </ul>
                <p>
                  We are committed to resolving all disputes fairly and promptly.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  For refund-related inquiries, please contact us:
                </p>
                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                  <p><strong>Email:</strong> refunds@intfinder.com</p>
                  <p><strong>Support:</strong> support@intfinder.com</p>
                  <p><strong>Response Time:</strong> 24-48 hours</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Company Information</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  <strong>Company Name:</strong> TECKVINI SOLUTIONS PRIVATE LIMITED<br/>
                  <strong>Product:</strong> IntFinder<br/>
                  <strong>Contact:</strong> For any refund-related questions, please contact us at refunds@intfinder.com
                </p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </PublicPageLayout>
  );
};
